import {
    Button,
    DatePicker,
    Drawer,
    Form,
    InputNumber,
    Select,
    Space,
    Table,
    message,
    Row,
    Col,
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import { useForm } from 'antd/es/form/Form'
import dayjs, { Dayjs } from 'dayjs'
import { FC, useEffect } from 'react'
import Contractor from '../../../../components/form-items/contractor/contractor'
import User from '../../../../components/form-items/user/user'
import { requiredRules } from '../../../../constants/forms/validation/rules'
import formatNumber from '../../../../helpers/formatters/format-any-number'
import renderDate from '../../../../helpers/tables/render-date'
import useErrorModal from '../../../../hooks/api/use-error-modal'
import {
    useCreateOrderMutation,
    useGetOrderByDocIdQuery,
    useUpdateOrderMutation,
} from '../../../../store/api/orders-api'
import { OrderStatus, PaymentTerms } from '../../../../types/api/order'
import OrderItem from '../../../../types/api/order-item'
import { orderDetailParams } from '../../constants/default-params'
import { resolveOrderItemRow } from '../../product-type-resolvers'
import {
    itemStatusLabels,
    orderItemUnitLabels,
    orderStatusOptions,
    paymentTermsOptions,
} from '../../constants/status-labels'

interface OrderDrawerProps {
    open: boolean
    orderDocumentId?: string
    mode: 'create' | 'edit'
    onClose: () => void
    onSaved: () => void
}

interface OrderFormValues {
    client?: string
    manager?: string
    orderStatus: OrderStatus
    dueDate?: Dayjs | null
    paymentTerms?: PaymentTerms
    deferralDays?: number | null
    discountPercent?: number | null
}

const itemColumns: ColumnsType<OrderItem> = [
    {
        title: 'Наименование',
        ellipsis: true,
        render: (_, record) => resolveOrderItemRow(record).name,
    },
    {
        title: 'Тип продукции',
        dataIndex: ['product_type', 'name'],
        width: 140,
        ellipsis: true,
    },
    {
        title: 'Кол-во',
        width: 100,
        render: (_, record) => {
            const quantity = resolveOrderItemRow(record).quantity
            return quantity != null ? formatNumber(quantity) : undefined
        },
    },
    {
        title: 'Ед.',
        dataIndex: 'unit',
        width: 90,
        render: (unit: OrderItem['unit']) =>
            unit ? (orderItemUnitLabels[unit] ?? unit) : undefined,
    },
    {
        title: 'Статус',
        dataIndex: 'itemStatus',
        width: 130,
        render: (status: OrderItem['itemStatus']) =>
            status ? (itemStatusLabels[status] ?? status) : undefined,
    },
    {
        title: 'Цена',
        width: 110,
        render: (_, record) => {
            const price = resolveOrderItemRow(record).price
            return price != null ? formatNumber(Math.round(price)) : undefined
        },
    },
    {
        title: 'Срок пр-ва',
        dataIndex: 'productionDueDate',
        width: 110,
        render: (v: string | undefined) => (v ? renderDate(v) : undefined),
    },
]

const OrderDrawer: FC<OrderDrawerProps> = ({ open, orderDocumentId, mode, onClose, onSaved }) => {
    const [form] = useForm<OrderFormValues>()
    const isEdit = mode === 'edit' && Boolean(orderDocumentId)

    const { data: orderResponse, isFetching } = useGetOrderByDocIdQuery(
        [orderDocumentId ?? '', orderDetailParams],
        { skip: !open || !isEdit || !orderDocumentId }
    )

    const [createOrder, createState] = useCreateOrderMutation()
    const [updateOrder, updateState] = useUpdateOrderMutation()

    useErrorModal(createState, 'Ошибка создания заказа')
    useErrorModal(updateState, 'Ошибка обновления заказа')

    const order = orderResponse?.data
    const paymentTerms = Form.useWatch('paymentTerms', form)

    useEffect(() => {
        if (!open) return

        if (mode === 'create') {
            form.setFieldsValue({
                client: undefined,
                manager: undefined,
                orderStatus: 'draft',
                dueDate: null,
                paymentTerms: undefined,
                deferralDays: null,
                discountPercent: null,
            })
            return
        }

        if (order) {
            form.setFieldsValue({
                client: order.client?.documentId,
                manager: order.manager?.documentId,
                orderStatus: order.orderStatus ?? 'draft',
                dueDate: order.dueDate ? dayjs(order.dueDate) : null,
                paymentTerms: order.paymentTerms,
                deferralDays: order.deferralDays ?? null,
                discountPercent: order.discountPercent ?? null,
            })
        }
    }, [open, mode, order, form])

    const buildPayload = (values: OrderFormValues) => {
        const data: Record<string, unknown> = {
            orderStatus: values.orderStatus,
            dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null,
            paymentTerms: values.paymentTerms ?? null,
            deferralDays: values.paymentTerms === 'deferral' ? (values.deferralDays ?? null) : null,
            discountPercent: values.discountPercent ?? null,
        }

        if (values.client) {
            data.client = { set: [values.client] }
        }
        if (values.manager) {
            data.manager = { set: [values.manager] }
        }

        return data
    }

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()
            const data = buildPayload(values)

            if (mode === 'create') {
                await createOrder({ data: data as never }).unwrap()
                message.success('Заказ создан')
            } else if (orderDocumentId) {
                await updateOrder({
                    docId: orderDocumentId,
                    body: { data: data as never },
                }).unwrap()
                message.success('Заказ сохранён')
            }

            onSaved()
            onClose()
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in error) {
                return
            }
        }
    }

    const isSaving = createState.isLoading || updateState.isLoading
    const title = mode === 'create' ? 'Новый заказ' : 'Заказ'

    return (
        <Drawer
            title={title}
            width={1100}
            open={open}
            onClose={onClose}
            destroyOnClose
            extra={
                <Space>
                    <Button onClick={onClose}>Отмена</Button>
                    <Button type='primary' loading={isSaving} onClick={handleSubmit}>
                        Сохранить
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout='vertical' disabled={isFetching && isEdit}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Contractor name='client' label='Клиент' rules={requiredRules} />
                    </Col>
                    <Col span={12}>
                        <User
                            name='manager'
                            label='Менеджер'
                            rules={requiredRules}
                            placeholder='Менеджер'
                        />
                    </Col>

                    <Col span={6}>
                        <Form.Item name='orderStatus' label='Статус' rules={requiredRules}>
                            <Select options={orderStatusOptions} />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item name='dueDate' label='Срок готовности'>
                            <DatePicker format='DD.MM.YYYY' style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name='paymentTerms' label='Условия оплаты'>
                            <Select allowClear options={paymentTermsOptions} />
                        </Form.Item>
                    </Col>
                    {paymentTerms === 'deferral' ? (
                        <Col span={3}>
                            <Form.Item name='deferralDays' label='Отсрочка (дней)'>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    ) : null}
                    <Col span={3}>
                        <Form.Item name='discountPercent' label='Скидка, %'>
                            <InputNumber min={0} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {isEdit ? (
                <Table
                    size='small'
                    rowKey={row => row.documentId}
                    columns={itemColumns}
                    dataSource={order?.order_items ?? []}
                    pagination={false}
                    loading={isFetching}
                    scroll={{ x: 900 }}
                    locale={{ emptyText: 'Нет позиций' }}
                    title={() => 'Позиции (только просмотр)'}
                />
            ) : null}
        </Drawer>
    )
}

export default OrderDrawer
